import React, { useState } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts'
import {
  Plus,
  Play,
  Save,
  TrendingDown,
  AlertTriangle,
  DollarSign,
  Home,
  Percent,
  Clock,
  Copy
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import type {
  StressTestScenario,
  StressTestParameters,
  ScenarioImpact,
  StressTestBuilderProps
} from '@/types/risk-assessment'

export function StressTestScenarioBuilder({
  scenarios,
  onRunScenario,
  onSaveScenario
}: StressTestBuilderProps) {
  const [activeScenario, setActiveScenario] = useState<StressTestScenario | null>(null)
  const [isCreatingNew, setIsCreatingNew] = useState(false)
  const [runningScenarios, setRunningScenarios] = useState<Set<string>>(new Set())
  const [scenarioResults, setScenarioResults] = useState<Record<string, ScenarioImpact>>({})

  const defaultScenario: StressTestScenario = {
    scenario_id: 'new',
    name: 'New Stress Test',
    type: 'custom',
    parameters: {
      interest_rate_change: 0,
      vacancy_rate_change: 0,
      market_value_change: 0,
      rent_growth_change: 0,
      operating_expense_change: 0,
      duration_months: 12
    },
    impact: {
      cash_flow_impact: 0,
      portfolio_value_impact: 0,
      equity_impact: 0,
      monthly_payment_increase: 0,
      properties_at_risk: [],
      recovery_time_months: 0
    }
  }

  const [newScenario, setNewScenario] = useState<StressTestScenario>(defaultScenario)

  const predefinedScenarios = [
    {
      name: 'Interest Rate Shock +2%',
      type: 'interest_rate' as const,
      description: 'Federal Reserve raises rates by 2 percentage points',
      parameters: { interest_rate_change: 2, duration_months: 24 }
    },
    {
      name: 'Interest Rate Shock +3%',
      type: 'interest_rate' as const,
      description: 'Aggressive monetary tightening with 3% rate increase',
      parameters: { interest_rate_change: 3, duration_months: 36 }
    },
    {
      name: 'Vacancy Crisis +10%',
      type: 'vacancy' as const,
      description: 'Economic downturn causes widespread job losses',
      parameters: { vacancy_rate_change: 10, duration_months: 18 }
    },
    {
      name: 'Market Crash -20%',
      type: 'market_downturn' as const,
      description: 'Real estate market correction similar to 2008',
      parameters: { market_value_change: -20, rent_growth_change: -5, duration_months: 24 }
    },
    {
      name: 'Recession Scenario',
      type: 'recession' as const,
      description: 'Multi-factor economic recession',
      parameters: {
        market_value_change: -15,
        vacancy_rate_change: 8,
        rent_growth_change: -3,
        operating_expense_change: 5,
        duration_months: 18
      }
    }
  ]

  const handleRunScenario = async (scenario: StressTestScenario) => {
    setRunningScenarios(prev => new Set(prev).add(scenario.scenario_id))
    try {
      const impact = await onRunScenario(scenario)
      setScenarioResults(prev => ({
        ...prev,
        [scenario.scenario_id]: impact
      }))
    } finally {
      setRunningScenarios(prev => {
        const newSet = new Set(prev)
        newSet.delete(scenario.scenario_id)
        return newSet
      })
    }
  }

  const handleSaveScenario = async () => {
    if (newScenario.name.trim()) {
      const scenarioToSave = {
        ...newScenario,
        scenario_id: `custom_${Date.now()}`
      }
      await onSaveScenario(scenarioToSave)
      setNewScenario(defaultScenario)
      setIsCreatingNew(false)
    }
  }

  const handleCreateFromTemplate = (template: typeof predefinedScenarios[0]) => {
    setNewScenario({
      ...defaultScenario,
      name: template.name,
      type: template.type,
      parameters: {
        ...defaultScenario.parameters,
        ...template.parameters
      }
    })
    setIsCreatingNew(true)
  }

  const updateScenarioParameter = (field: keyof StressTestParameters, value: number) => {
    setNewScenario(prev => ({
      ...prev,
      parameters: {
        ...prev.parameters,
        [field]: value
      }
    }))
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const formatPercentage = (value: number) => {
    return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`
  }

  const getSeverityColor = (impact: number) => {
    const absImpact = Math.abs(impact)
    if (absImpact <= 5) return 'text-green-600'
    if (absImpact <= 15) return 'text-yellow-600'
    if (absImpact <= 30) return 'text-orange-600'
    return 'text-red-600'
  }

  const getSeverityBg = (impact: number) => {
    const absImpact = Math.abs(impact)
    if (absImpact <= 5) return 'bg-green-50 border-green-200'
    if (absImpact <= 15) return 'bg-yellow-50 border-yellow-200'
    if (absImpact <= 30) return 'bg-orange-50 border-orange-200'
    return 'bg-red-50 border-red-200'
  }

  // Prepare chart data
  const comparisonData = scenarios.map(scenario => {
    const result = scenarioResults[scenario.scenario_id]
    return {
      name: scenario.name.length > 15 ? scenario.name.substring(0, 15) + '...' : scenario.name,
      cash_flow_impact: result?.cash_flow_impact || 0,
      portfolio_value_impact: result?.portfolio_value_impact || 0,
      recovery_months: result?.recovery_time_months || 0
    }
  }).filter(item => item.cash_flow_impact !== 0 || item.portfolio_value_impact !== 0)

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Stress Test Scenarios</h3>
          <p className="text-gray-600 text-sm">
            Test your portfolio's resilience against market shocks and economic downturns
          </p>
        </div>
        <Button
          onClick={() => setIsCreatingNew(true)}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create Custom Scenario
        </Button>
      </div>

      {/* Predefined Scenario Templates */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="text-md font-semibold text-gray-900 mb-4">Quick Start Templates</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {predefinedScenarios.map((template, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors">
              <div className="flex items-start justify-between mb-2">
                <h5 className="font-medium text-gray-900">{template.name}</h5>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => handleCreateFromTemplate(template)}
                  className="ml-2"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              <p className="text-sm text-gray-600 mb-3">{template.description}</p>
              <div className="space-y-1 text-xs">
                {template.parameters.interest_rate_change && (
                  <div className="flex justify-between">
                    <span>Interest Rate:</span>
                    <span className="font-medium">{formatPercentage(template.parameters.interest_rate_change)}</span>
                  </div>
                )}
                {template.parameters.vacancy_rate_change && (
                  <div className="flex justify-between">
                    <span>Vacancy Rate:</span>
                    <span className="font-medium">{formatPercentage(template.parameters.vacancy_rate_change)}</span>
                  </div>
                )}
                {template.parameters.market_value_change && (
                  <div className="flex justify-between">
                    <span>Property Values:</span>
                    <span className="font-medium">{formatPercentage(template.parameters.market_value_change)}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Custom Scenario Builder */}
      {isCreatingNew && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-md font-semibold text-gray-900">Custom Stress Test Scenario</h4>
            <div className="flex items-center space-x-3">
              <Button
                variant="secondary"
                onClick={() => setIsCreatingNew(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveScenario}
                className="flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save Scenario
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Scenario Details */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Scenario Name
                </label>
                <input
                  type="text"
                  value={newScenario.name}
                  onChange={(e) => setNewScenario(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter scenario name..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Scenario Type
                </label>
                <select
                  value={newScenario.type}
                  onChange={(e) => setNewScenario(prev => ({ ...prev, type: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="custom">Custom</option>
                  <option value="interest_rate">Interest Rate Shock</option>
                  <option value="vacancy">Vacancy Crisis</option>
                  <option value="market_downturn">Market Downturn</option>
                  <option value="recession">Recession</option>
                </select>
              </div>
            </div>

            {/* Parameter Controls */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Percent className="w-4 h-4 inline mr-1" />
                    Interest Rate Change (%)
                  </label>
                  <input
                    type="number"
                    value={newScenario.parameters.interest_rate_change || 0}
                    onChange={(e) => updateScenarioParameter('interest_rate_change', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    step="0.1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Home className="w-4 h-4 inline mr-1" />
                    Vacancy Rate Change (%)
                  </label>
                  <input
                    type="number"
                    value={newScenario.parameters.vacancy_rate_change || 0}
                    onChange={(e) => updateScenarioParameter('vacancy_rate_change', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    step="0.1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <TrendingDown className="w-4 h-4 inline mr-1" />
                    Property Value Change (%)
                  </label>
                  <input
                    type="number"
                    value={newScenario.parameters.market_value_change || 0}
                    onChange={(e) => updateScenarioParameter('market_value_change', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    step="0.1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <DollarSign className="w-4 h-4 inline mr-1" />
                    Rent Growth Change (%)
                  </label>
                  <input
                    type="number"
                    value={newScenario.parameters.rent_growth_change || 0}
                    onChange={(e) => updateScenarioParameter('rent_growth_change', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    step="0.1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <TrendingDown className="w-4 h-4 inline mr-1" />
                    Operating Expense Change (%)
                  </label>
                  <input
                    type="number"
                    value={newScenario.parameters.operating_expense_change || 0}
                    onChange={(e) => updateScenarioParameter('operating_expense_change', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    step="0.1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Duration (Months)
                  </label>
                  <input
                    type="number"
                    value={newScenario.parameters.duration_months || 12}
                    onChange={(e) => updateScenarioParameter('duration_months', parseInt(e.target.value) || 12)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                    max="120"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Existing Scenarios */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="text-md font-semibold text-gray-900 mb-4">Saved Scenarios</h4>
        <div className="space-y-4">
          {scenarios.map((scenario) => {
            const isRunning = runningScenarios.has(scenario.scenario_id)
            const result = scenarioResults[scenario.scenario_id]

            return (
              <div
                key={scenario.scenario_id}
                className={`border rounded-lg p-4 ${
                  result ? getSeverityBg(result.portfolio_value_impact) : 'border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h5 className="font-medium text-gray-900">{scenario.name}</h5>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                      <span className="capitalize">{scenario.type.replace('_', ' ')}</span>
                      <span>Duration: {scenario.parameters.duration_months} months</span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleRunScenario(scenario)}
                    disabled={isRunning}
                    className="flex items-center gap-2"
                  >
                    {isRunning ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <Play className="w-3 h-3" />
                    )}
                    {isRunning ? 'Running...' : 'Run Test'}
                  </Button>
                </div>

                {/* Parameters Summary */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm mb-3">
                  {scenario.parameters.interest_rate_change !== 0 && (
                    <div>
                      <span className="text-gray-500">Interest Rate:</span>
                      <div className="font-medium">{formatPercentage(scenario.parameters.interest_rate_change!)}</div>
                    </div>
                  )}
                  {scenario.parameters.vacancy_rate_change !== 0 && (
                    <div>
                      <span className="text-gray-500">Vacancy:</span>
                      <div className="font-medium">{formatPercentage(scenario.parameters.vacancy_rate_change!)}</div>
                    </div>
                  )}
                  {scenario.parameters.market_value_change !== 0 && (
                    <div>
                      <span className="text-gray-500">Property Value:</span>
                      <div className="font-medium">{formatPercentage(scenario.parameters.market_value_change!)}</div>
                    </div>
                  )}
                  {scenario.parameters.rent_growth_change !== 0 && (
                    <div>
                      <span className="text-gray-500">Rent Growth:</span>
                      <div className="font-medium">{formatPercentage(scenario.parameters.rent_growth_change!)}</div>
                    </div>
                  )}
                  {scenario.parameters.operating_expense_change !== 0 && (
                    <div>
                      <span className="text-gray-500">Expenses:</span>
                      <div className="font-medium">{formatPercentage(scenario.parameters.operating_expense_change!)}</div>
                    </div>
                  )}
                </div>

                {/* Results */}
                {result && (
                  <div className="border-t border-gray-200 pt-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Cash Flow Impact:</span>
                        <div className={`font-bold ${getSeverityColor(result.cash_flow_impact)}`}>
                          {formatCurrency(result.cash_flow_impact)}/month
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-500">Portfolio Impact:</span>
                        <div className={`font-bold ${getSeverityColor(result.portfolio_value_impact)}`}>
                          {formatCurrency(result.portfolio_value_impact)}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-500">Properties at Risk:</span>
                        <div className="font-medium text-gray-900">
                          {result.properties_at_risk.length}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-500">Recovery Time:</span>
                        <div className="font-medium text-gray-900">
                          {result.recovery_time_months} months
                        </div>
                      </div>
                    </div>

                    {result.properties_at_risk.length > 0 && (
                      <div className="mt-3 flex items-center space-x-2">
                        <AlertTriangle className="w-4 h-4 text-orange-600" />
                        <span className="text-sm text-orange-800">
                          {result.properties_at_risk.length} properties require immediate attention
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Results Comparison Chart */}
      {comparisonData.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="text-md font-semibold text-gray-900 mb-4">Scenario Impact Comparison</h4>
          <div style={{ width: '100%', height: 400 }}>
            <ResponsiveContainer>
              <BarChart data={comparisonData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  tickFormatter={(value) => formatCurrency(value)}
                />
                <Tooltip
                  formatter={(value: number, name: string) => [
                    formatCurrency(value),
                    name === 'portfolio_value_impact' ? 'Portfolio Impact' : 'Cash Flow Impact'
                  ]}
                  labelFormatter={(label) => `Scenario: ${label}`}
                />
                <Bar dataKey="cash_flow_impact" fill="#ef4444" name="Monthly Cash Flow Impact" />
                <Bar dataKey="portfolio_value_impact" fill="#f97316" name="Portfolio Value Impact" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  )
}